import React, { useEffect, useState } from "react";
import { Typography, Stack, Box, Button } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { UserType } from "../../../../utils/constants";

import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import useAuth from "../../../../hooks/useAuth";
import { useNotification } from "../../../../contexts/NotificationContext";
import { SaveButton, CancelButton } from "../../../common/Button";

import { ReactComponent as UploadIcon } from "../../../../vendor/_new/icon/sites/upload.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import colors from "../../../../utils/colors";

function SSLConfigurationUpload({ open, siteUid, handleClose }) {
  const { userType } = useAuth();
  const { configSslSetting, errMsg } = useSSLConfig();
  const { showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [fullchain, setFullchain] = useState("Upload your own fullchain");
  const [privkey, setPrivkey] = useState("Upload your own privkey");

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  useEffect(() => {
    if (open) {
      setFullchain("Upload your own fullchain");
      setPrivkey("Upload your own privkey");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = (event, cate) => {
    if (event.target.files.length === 0) {
      return;
    }
    const file = event.target.files[0];
    let reader = new FileReader();
    reader.readAsText(file);
    reader.onload = function () {
      switch (cate) {
        case "fullchain":
          setFullchain(reader.result);
          break;
        case "privkey":
          setPrivkey(reader.result);
          break;
        default:
          break;
      }
    };
  };
  const handleSave = async () => {
    setLoading(true);
    const result = await configSslSetting(siteUid, "uploadCert", { fullchain, privkey });
    setLoading(false);
    if (result) handleClose();
  };
  return (
    <>
      <Stack spacing={3} justifyContent={"space-between"} sx={{ height: "100%" }}>
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography
              sx={{
                fontWeight: "medium",
                fontSize: 18,
                lineHeight: "24px",
                color: colors.neutral[1000],
              }}
            >
              Upload SSL certificate
            </Typography>
            <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
              Secure your domain by uploading a valid SSL/TLS certificate.
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            sx={{ width: "100%", height: 110, border: `solid 1px ${colors.neutral[300]}`, borderRadius: "8px", p: 4 }}
          >
            <Box sx={{ width: `calc(100% - 32px)`, height: 78 }}>
              <ReactPerfectScrollbar>
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                  {fullchain}
                </Typography>
              </ReactPerfectScrollbar>
            </Box>
            {userType !== UserType.ORG_USER ? (
              <Button
                component="label"
                variant="text"
                color="primary"
                startIcon={<UploadIcon style={{ color: colors.neutral[600] }} />}
                sx={{ minWidth: "inherit", p: `0px!important`, width: 24, height: 24 }}
              >
                <input accept=".pem" type="file" onChange={(event) => handleFileUpload(event, "fullchain")} hidden />
              </Button>
            ) : (
              <></>
            )}
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            sx={{ width: "100%", height: 110, border: `solid 1px ${colors.neutral[300]}`, borderRadius: "8px", p: 4 }}
          >
            <Box sx={{ width: `calc(100% - 32px)`, height: 78 }}>
              <ReactPerfectScrollbar>
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                  {privkey}
                </Typography>
              </ReactPerfectScrollbar>
            </Box>
            {userType !== UserType.ORG_USER ? (
              <Button
                component="label"
                variant="text"
                color="primary"
                startIcon={<UploadIcon style={{ color: colors.neutral[600] }} />}
                sx={{ minWidth: "inherit", p: `0px!important`, width: 24, height: 24 }}
              >
                <input accept=".pem" type="file" hidden onChange={(event) => handleFileUpload(event, "privkey")} />
              </Button>
            ) : (
              <></>
            )}
          </Stack>
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              minHeight: 48,
              p: 2,
              borderRadius: "8px",
              backgroundColor: colors.primary["alpha"],
              border: `solid 1px ${colors.primary[300]}`,
            }}
          >
            <Box sx={{ width: 24, height: 24 }}>
              <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
            </Box>
            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
              You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={"row"} alignItems={"center"} spacing={3}>
          {userType !== UserType.ORG_USER ? <SaveButton disabled={loading} onClick={handleSave} /> : <></>}
          <CancelButton onClick={handleClose} />
        </Stack>
      </Stack>
    </>
  );
}

export default SSLConfigurationUpload;
